/**
 * ChartLegendView - Displays chart legend with status indicators
 *
 * Renders legend items for Started, Sub, and Did Not Play statuses.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

/**
 * Props for ChartLegendView component
 */
interface ChartLegendViewProps {
  readonly isDark: boolean;
}

/**
 * ChartLegendView renders the chart legend
 * @param props - View props
 * @param props.isDark - Whether dark theme is active for styling
 * @returns HStack containing legend items
 */
const ChartLegendView = ({ isDark }: ChartLegendViewProps) => (
  <Stack
    direction="horizontal"
    {...{
      testID: TEST_ID_PLAYER_DETAIL.CHART_LEGEND,
      "data-testid": TEST_ID_PLAYER_DETAIL.CHART_LEGEND,
      accessibilityLabel: "Chart legend",
      "aria-label": "Chart legend",
    }}
    className="justify-center gap-4"
  >
    <Stack direction="horizontal" className="items-center gap-2">
      <Box className="size-3 rounded-full bg-status-success" />
      <Text variant="body" className="text-content-muted">
        Started
      </Text>
    </Stack>
    <Stack direction="horizontal" className="items-center gap-2">
      <Box className="size-3 rounded-full bg-status-info" />
      <Text variant="body" className="text-content-muted">
        Sub
      </Text>
    </Stack>
    <Stack direction="horizontal" className="items-center gap-2">
      <Box
        className={`size-3 rounded-full border-2 border-dashed ${isDark ? "border-content-muted" : "border-content-subtle"}`}
      />
      <Text variant="body" className="text-content-muted">
        DNP
      </Text>
    </Stack>
  </Stack>
);

ChartLegendView.displayName = "ChartLegendView";

export default memo(ChartLegendView);
